import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { FaShieldAlt, FaLock, FaUserShield, FaClipboardCheck, FaNetworkWired, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa'
import PageHero from '../../components/PageHero/PageHero'

const InternalControlExaminations = () => {
  return (
    <>
      <Helmet>
        <title>Internal Control Examinations - Proof of Reserves Auditor</title>
        <meta name="description" content="SOC 2, SOC 1, and custom internal control examinations for cryptocurrency and digital asset companies." />
      </Helmet>

      <div className="service-page">
        <PageHero
          title="Internal Control Examinations"
          description="Comprehensive evaluation of your control environment to ensure security, compliance, and operational excellence"
        >
          <div className="flex flex-wrap gap-6 mb-8 justify-center">
            <div className="flex items-center space-x-2">
              <FaCheckCircle className="text-accent" />
              <span>SOC 2 Type II</span>
            </div>
            <div className="flex items-center space-x-2">
              <FaCheckCircle className="text-accent" />
              <span>SOC 1 Reports</span>
            </div>
            <div className="flex items-center space-x-2">
              <FaCheckCircle className="text-accent" />
              <span>Custom Examinations</span>
            </div>
          </div>
          <Link to="/contact" className="btn-primary bg-accent hover:bg-accent-dark">
            Request Assessment
          </Link>
        </PageHero>

        {/* Overview Section */}
        <section className="section-padding bg-white">
          <div className="container-custom max-w-4xl">
            <h2 className="section-title">Overview</h2>
            <p className="text-lg text-gray-600 mb-6">
              Internal control examinations provide independent assurance that your organization has implemented effective controls to protect customer data, ensure transaction integrity, and maintain operational security. Our examinations are tailored to the unique risks and requirements of cryptocurrency and digital asset businesses.
            </p>
            <p className="text-lg text-gray-600">
              Whether you need SOC 2 Type II reports for enterprise customers, SOC 1 reports for service organizations, or custom control assessments, our crypto-native auditors deliver comprehensive evaluations that strengthen your security posture and build stakeholder confidence.
            </p>
          </div>
        </section>

        {/* Types of Examinations */}
        <section className="section-padding bg-gray-50">
          <div className="container-custom">
            <h2 className="section-title text-center mb-12">Types of Examinations We Perform</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-lg shadow-md">
                <FaShieldAlt className="text-5xl text-accent mb-4" />
                <h3 className="text-2xl font-bold mb-4 text-primary">SOC 2 Type II</h3>
                <p className="text-gray-600 mb-4">
                  Comprehensive examination of your controls related to security, availability, processing integrity, confidentiality, and privacy.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <FaCheckCircle className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>6-12 month observation period</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheckCircle className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Operating effectiveness testing</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheckCircle className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Enterprise customer requirement</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-md">
                <FaClipboardCheck className="text-5xl text-accent mb-4" />
                <h3 className="text-2xl font-bold mb-4 text-primary">SOC 1 Reports</h3>
                <p className="text-gray-600 mb-4">
                  Examination of controls relevant to user entities' financial reporting, critical for service organizations.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <FaCheckCircle className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Financial reporting controls</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheckCircle className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Type I or Type II available</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheckCircle className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Service organization focus</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-md">
                <FaNetworkWired className="text-5xl text-accent mb-4" />
                <h3 className="text-2xl font-bold mb-4 text-primary">Custom Examinations</h3>
                <p className="text-gray-600 mb-4">
                  Tailored control assessments addressing specific risks, regulatory requirements, or stakeholder needs.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <FaCheckCircle className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Wallet security controls</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheckCircle className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Smart contract governance</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheckCircle className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Regulatory compliance controls</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Control Areas */}
        <section className="section-padding bg-white">
          <div className="container-custom">
            <h2 className="section-title text-center mb-12">Key Control Areas We Examine</h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <div className="border-l-4 border-accent pl-6">
                <div className="flex items-center mb-3">
                  <FaLock className="text-2xl text-accent mr-3" />
                  <h3 className="text-xl font-bold text-primary">Access Controls</h3>
                </div>
                <p className="text-gray-600">
                  User authentication, authorization, privileged access management, and segregation of duties across systems and wallets.
                </p>
              </div>

              <div className="border-l-4 border-accent pl-6">
                <div className="flex items-center mb-3">
                  <FaShieldAlt className="text-2xl text-accent mr-3" />
                  <h3 className="text-xl font-bold text-primary">Cryptographic Key Management</h3>
                </div>
                <p className="text-gray-600">
                  Private key generation, storage, backup, and usage controls including multi-signature requirements and HSM implementation.
                </p>
              </div>

              <div className="border-l-4 border-accent pl-6">
                <div className="flex items-center mb-3">
                  <FaNetworkWired className="text-2xl text-accent mr-3" />
                  <h3 className="text-xl font-bold text-primary">Change Management</h3>
                </div>
                <p className="text-gray-600">
                  Smart contract deployment processes, code review procedures, testing protocols, and production change controls.
                </p>
              </div>

              <div className="border-l-4 border-accent pl-6">
                <div className="flex items-center mb-3">
                  <FaUserShield className="text-2xl text-accent mr-3" />
                  <h3 className="text-xl font-bold text-primary">Incident Response</h3>
                </div>
                <p className="text-gray-600">
                  Security incident detection, response procedures, escalation protocols, and post-incident review processes.
                </p>
              </div>

              <div className="border-l-4 border-accent pl-6">
                <div className="flex items-center mb-3">
                  <FaClipboardCheck className="text-2xl text-accent mr-3" />
                  <h3 className="text-xl font-bold text-primary">Monitoring & Logging</h3>
                </div>
                <p className="text-gray-600">
                  Transaction monitoring, blockchain monitoring, system logging, alert mechanisms, and log retention policies.
                </p>
              </div>

              <div className="border-l-4 border-accent pl-6">
                <div className="flex items-center mb-3">
                  <FaExclamationTriangle className="text-2xl text-accent mr-3" />
                  <h3 className="text-xl font-bold text-primary">Risk Management</h3>
                </div>
                <p className="text-gray-600">
                  Risk assessment processes, risk mitigation strategies, third-party risk management, and continuous risk monitoring.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="section-padding bg-gray-50">
          <div className="container-custom max-w-4xl">
            <h2 className="section-title">Benefits of Internal Control Examinations</h2>
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="font-bold text-lg mb-2 text-primary">Win Enterprise Customers</h3>
                <p className="text-gray-600">
                  SOC 2 reports are increasingly required by enterprise customers to demonstrate your commitment to security and data protection.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="font-bold text-lg mb-2 text-primary">Strengthen Security Posture</h3>
                <p className="text-gray-600">
                  Independent examination identifies control gaps and weaknesses, allowing you to strengthen your security before incidents occur.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="font-bold text-lg mb-2 text-primary">Regulatory Compliance</h3>
                <p className="text-gray-600">
                  Demonstrate to regulators that you maintain effective controls over customer assets, data, and transactions.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="font-bold text-lg mb-2 text-primary">Investor Confidence</h3>
                <p className="text-gray-600">
                  Professional control assessments provide investors with assurance about your operational maturity and risk management.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="font-bold text-lg mb-2 text-primary">Insurance Requirements</h3>
                <p className="text-gray-600">
                  Cyber insurance carriers often require SOC 2 reports or control assessments to provide coverage or reduce premiums.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="font-bold text-lg mb-2 text-primary">Competitive Advantage</h3>
                <p className="text-gray-600">
                  Differentiate yourself in the market by demonstrating superior security and operational controls through independent validation.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Examination Process */}
        <section className="section-padding bg-white">
          <div className="container-custom max-w-4xl">
            <h2 className="section-title">Our Examination Process</h2>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="bg-accent text-white rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 font-bold">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-primary">Scoping & Planning</h3>
                  <p className="text-gray-600">
                    We work with you to define examination scope, identify systems in scope, determine testing period, and establish timelines.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-accent text-white rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 font-bold">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-primary">Control Documentation Review</h3>
                  <p className="text-gray-600">
                    Review of your control documentation, policies, procedures, and system descriptions to understand your control environment.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-accent text-white rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 font-bold">
                  3
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-primary">Walkthrough Procedures</h3>
                  <p className="text-gray-600">
                    Interviews with key personnel and walkthroughs of control processes to validate control design effectiveness.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-accent text-white rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 font-bold">
                  4
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-primary">Testing of Operating Effectiveness</h3>
                  <p className="text-gray-600">
                    Detailed testing of control operation over the examination period through sampling, inspection, and observation.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-accent text-white rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 font-bold">
                  5
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-primary">Findings Communication</h3>
                  <p className="text-gray-600">
                    Regular communication of preliminary findings, allowing you to remediate issues before report finalization.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-accent text-white rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 font-bold">
                  6
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-primary">Report Issuance</h3>
                  <p className="text-gray-600">
                    Delivery of final examination report with our opinion on control design and operating effectiveness.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Who Needs This */}
        <section className="section-padding bg-gray-50">
          <div className="container-custom">
            <h2 className="section-title text-center mb-12">Who Needs Internal Control Examinations?</h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-3 text-primary">Exchanges & Trading Platforms</h3>
                <p className="text-gray-600">
                  Demonstrate to customers and regulators that you have robust controls over customer assets, order execution, and data security.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-3 text-primary">Custodians & Wallet Providers</h3>
                <p className="text-gray-600">
                  Prove your control environment for safeguarding customer private keys and securing digital asset custody operations.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-3 text-primary">DeFi Protocols</h3>
                <p className="text-gray-600">
                  Validate governance processes, smart contract deployment controls, and treasury management procedures.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-3 text-primary">Enterprise Service Providers</h3>
                <p className="text-gray-600">
                  SOC 2 Type II is often required to win and retain enterprise customers who demand proven security and compliance.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-padding bg-gradient-to-br from-primary to-primary-dark text-white">
          <div className="container-custom text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Strengthen Your Controls?
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Schedule a consultation to discuss your internal control examination needs and readiness assessment.
            </p>
            <Link to="/contact" className="btn-primary bg-accent hover:bg-accent-dark">
              Schedule Consultation
            </Link>
          </div>
        </section>
      </div>
    </>
  )
}

export default InternalControlExaminations